import { MmmFeatureNameResponse } from '@blisspointmedia/bpm-types/dist/MMMFeatureNames';
import { isRawFeatureId, lookupFeatureIdentity } from '@src/utils/mmm-feature-mapping';
import { FeatureMapsByModelType } from './useFeatureMapping';

// WS4 "resolution": turn a new-format (client `away`) MMM output row — populated `feature_id`,
// blank `name`/`channel` — into a row that looks exactly like an old-format row so every existing
// downstream parser (parseChannelColumn / parseChannelName / the decay + saturation transforms)
// keeps working unchanged. Old-format rows (name already populated) are never touched here, so
// poppi / tinuititest behavior is unaffected.

// Segment values the backend uses for "no value" — normalized to empty so they don't leak into
// the reconstructed display name (matches the "none"/"N/A" handling already in parseMediaChannel).
const NA_SEGMENT_VALUES = new Set(['', 'n/a', 'na', 'none', 'null', 'undefined']);

const isMeaningfulSegment = (value: string | undefined | null): value is string =>
  typeof value === 'string' && !NA_SEGMENT_VALUES.has(value.trim().toLowerCase());

const isBlank = (value: unknown): boolean => value == null || String(value).trim().length === 0;

// Dimension slugs ordered by the hierarchy `keys` (e.g. ["channel","platform","tactic",
// "brand-nonbrand"]). Reading order from data means a backend change to segment count/order flows
// through automatically instead of breaking a hardcoded positional split.
const orderedSlugs = (map: MmmFeatureNameResponse): string[] =>
  Object.keys(map.keys).sort((a, b) => map.keys[a] - map.keys[b]);

export interface ResolvedFeatureParts {
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brand_nonbrand: string;
}

// Resolve a feature id to per-dimension display values via the map's `fields`, read in `keys`
// order: slug 0 -> channel, 1 -> platform, 2 -> tactic, 3 -> brand_nonbrand. The reconstructed
// `name` is the meaningful parts joined by ", " — the comma delimiter every "new format" branch in
// the HROAS parsers already expects ("Channel, Platform, ..."), NOT the map's " / "-joined
// `identity.name` (which the comma-splitting parsers would collapse into a single channel token).
// Falls back to `identity.name` only when no per-dimension segment is meaningful.
const resolveParts = (
  featureId: string,
  map: MmmFeatureNameResponse
): ResolvedFeatureParts | null => {
  // featureIdMap keys are uppercase (e.g. "FEATID378"); a raw id can reach the UI in any case.
  const identity = lookupFeatureIdentity(featureId, map);
  if (!identity) {
    return null;
  }
  const slugs = orderedSlugs(map);
  const valueAt = (index: number): string => {
    const slug = slugs[index];
    const raw = slug ? identity.fields[slug] : '';
    return isMeaningfulSegment(raw) ? raw.trim() : '';
  };
  const channel = valueAt(0);
  const platform = valueAt(1);
  const tactic = valueAt(2);
  const brand_nonbrand = valueAt(3);
  const parts = [channel, platform, tactic, brand_nonbrand].filter(isMeaningfulSegment);
  const name = parts.length > 0 ? parts.join(', ') : identity.name;
  return { name, channel, platform, tactic, brand_nonbrand };
};

// Typed display fields the resolver reads/writes on SES / MDD rows. No index signature, so typed
// interface rows (SpendEffectShareCsvRow / MddRow) still satisfy the generic constraint and keep
// their full type through the resolver.
interface FeatureRowShape {
  name?: string;
  feature_id?: string;
  channel?: string;
  platform?: string;
  tactic?: string;
  brand_nonbrand?: string;
}

// Core discriminator + resolution, shared by the typed-row and raw-record entry points.
//
// Gate (the new-format discriminator): `name` is blank AND `feature_id` is populated. Old-format
// rows (name populated) yield `null` (no patch) — this is the safety that keeps poppi / tinuititest
// untouched. New-format rows get a full field patch from featureIdMap[feature_id]:
//   - map hit  -> name + channel/platform/tactic/brand_nonbrand from the resolved parts
//   - map miss -> surface the feature id itself (universal fallback `... || feature_id`) so the row
//                 shows the id rather than "Unknown".
const computeRowPatch = (
  name: unknown,
  featureId: unknown,
  map: MmmFeatureNameResponse | undefined
): ResolvedFeatureParts | null => {
  if (!isBlank(name) || isBlank(featureId)) {
    return null;
  }
  const id = String(featureId);
  const resolved = map ? resolveParts(id, map) : null;
  if (!resolved) {
    return { name: id, channel: id, platform: '', tactic: '', brand_nonbrand: '' };
  }
  return {
    name: resolved.name,
    channel: resolved.channel || resolved.name,
    platform: resolved.platform,
    tactic: resolved.tactic,
    brand_nonbrand: resolved.brand_nonbrand
  };
};

// Normalize a typed SES / MDD row IN THE ADAPTER LAYER so downstream rendering is format-agnostic.
// Returns the row unchanged for old-format rows; otherwise merges the resolved display fields.
export const resolveFeatureRow = <T extends FeatureRowShape>(
  row: T,
  map: MmmFeatureNameResponse | undefined
): T => {
  const patch = computeRowPatch(row.name, row.feature_id, map);
  return patch ? { ...row, ...patch } : row;
};

// Same normalization for the raw `Record<string, unknown>` response-curve rows, which are not a
// typed interface. Preserves every other column on the row (spend/response/curve_point/...).
export const resolveFeatureRecord = (
  row: Record<string, unknown>,
  map: MmmFeatureNameResponse | undefined
): Record<string, unknown> => {
  const patch = computeRowPatch(row.name, row.feature_id, map);
  return patch ? { ...row, ...patch } : row;
};

// Multi-map variants. HROAS blends rMMM + GeoMMM + BrandEquity rows on one page simultaneously, so
// a single global feature map isn't enough — each row must resolve against the map for ITS OWN
// model_type. `getModelType` is a per-row accessor (not one global model type) so a differently
// shaped row (e.g. a sankey / two-stage-decomposition row built in a separate PR) can reuse these
// without needing to satisfy FeatureRowShape at the call site — only the accessor needs to know how
// to read that row's model_type. `maps` is keyed identically to `HROAS_MODEL_ID_TO_FEATURE_MAPPING_TYPE`'s
// values (e.g. "rMMM"/"GeoMMM"/"BrandEquity"); callers remap a raw `model_type` through that table
// before/inside `getModelType` if the row carries the raw key instead.
export const resolveFeatureRowMultiMap = <T extends FeatureRowShape>(
  row: T,
  getModelType: (row: T) => string | undefined,
  maps: FeatureMapsByModelType | undefined
): T => resolveFeatureRow(row, maps?.[getModelType(row) ?? '']);

// Same multi-map dispatch for the raw `Record<string, unknown>` response-curve rows.
export const resolveFeatureRecordMultiMap = (
  row: Record<string, unknown>,
  getModelType: (row: Record<string, unknown>) => string | undefined,
  maps: FeatureMapsByModelType | undefined
): Record<string, unknown> => resolveFeatureRecord(row, maps?.[getModelType(row) ?? '']);

// Decay-path resolution. The getDecayCurves lambda rows carry only a `name` column (no
// feature_id), so gate on the name being a RAW feature id: raw ids resolve to the reconstructed
// comma-joined name; anything already human-readable (old clients) passes through untouched.
export const resolveRawFeatureName = (
  name: string | undefined | null,
  map: MmmFeatureNameResponse | undefined
): string => {
  const value = name == null ? '' : String(name);
  if (!map || !value.trim() || !isRawFeatureId(value)) {
    return value;
  }
  const resolved = resolveParts(value, map);
  return resolved ? resolved.name : value;
};

// Decay-path helper for rows that DO carry a dedicated `feature_id` column (getDecayCurves emits one
// as of #186). Resolve that id to the comma-joined display name (channel, platform, ...) exactly the
// way SES / RC / MDD reconstruct names — the comma delimiter the decay transforms
// (transformHolisticDecayCsvToDecayData / transformCsvToDecayDataArray) split on, NOT the map's
// " / "-joined identity string. map hit -> reconstructed comma-joined name; map miss -> the feature
// id itself (never "Unknown"), matching computeRowPatch's fallback. Blank id -> "".
export const resolveFeatureIdToName = (
  featureId: string | undefined | null,
  map: MmmFeatureNameResponse | undefined
): string => {
  const id = featureId == null ? '' : String(featureId).trim();
  if (!id) {
    return '';
  }
  const resolved = map ? resolveParts(id, map) : null;
  return resolved ? resolved.name : id;
};
